import type { LearningPath, Unit, Lesson } from './lessons';

export type { LearningPath, Unit, Lesson };

/**
 * Baseball curriculum — mirrors the structure of basketballLearningPaths so all
 * existing components (HomeTab, LearnTab, SkillTree, SkillLevelPicker, hearts
 * practice, coach lesson picker, achievements/XP/streak engine) consume it
 * without any engine changes.
 */
export const baseballLearningPaths: LearningPath[] = [
  // ═══════════════════════════════════════════════════════════════
  // PATH 1 — THE BASICS (Beginner)
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'baseball-basics',
    difficulty: 'beginner',
    title: 'The Basics',
    description: 'Innings, positions, and the rules that drive every pitch.',
    color: 'primary',
    units: [
      {
        id: 'bb-basics-101',
        title: 'Baseball Basics',
        icon: '⚾',
        requiredXp: 0,
        lessons: [
          {
            id: 'baseball-understanding-innings',
            title: 'Understanding Innings',
            description: 'How a baseball game is structured.',
            conceptText: [
              'A standard baseball game is played over 9 innings. Each inning has two halves: the top (away team bats) and the bottom (home team bats).',
              'A half-inning ends when the team in the field gets 3 outs. After 3 outs, the teams switch — batting team goes out to defend, fielding team comes in to bat.',
              'If the score is tied after 9 full innings, extra innings are played until one team is ahead at the end of an inning. The home team always bats last, giving them a slight strategic advantage.',
            ],
            xpReward: 15,
            questions: [
              {
                id: 'bsb-inn-q1',
                type: 'multiple-choice',
                question: 'How many outs does each team need to record to end a half-inning?',
                options: ['1', '2', '3', '4'],
                correctIndex: 2,
                explanation: 'Three outs end a half-inning. After the third out, the teams swap roles — the batting team goes out to field, and vice versa.',
              },
              {
                id: 'bsb-inn-tf1',
                type: 'true-false',
                question: 'The home team always bats in the bottom of the inning.',
                options: [],
                correctIndex: 0,
                correctAnswer: true,
                explanation: 'True. The visiting team bats in the top, the home team bats in the bottom — meaning the home team always gets last licks.',
              },
              {
                id: 'bsb-inn-q2',
                type: 'multiple-choice',
                question: 'A regulation MLB game lasts how many innings (without extras)?',
                options: ['7', '9', '10', '12'],
                correctIndex: 1,
                explanation: 'A regulation MLB game is 9 innings. If tied, the game continues into extra innings until a winner is decided.',
              },
            ],
          },
          {
            id: 'baseball-outs-half-innings',
            title: 'Outs & Half-Innings',
            description: 'How outs are recorded and why they matter.',
            conceptText: [
              'A batter or runner can be put out in many ways: a strikeout, a fly ball caught in the air, a ground ball thrown to first before the runner arrives, or being tagged off the base.',
              'Once 3 outs are recorded, every runner on base is "stranded" — they don\'t score. This is why situational hitting (advancing runners with less than 2 outs) is so valuable.',
              'Outs are the most important resource in baseball. Unlike timed sports, you can\'t run out the clock — you can only burn outs. Every out spent must move the game forward.',
            ],
            xpReward: 15,
            questions: [
              {
                id: 'bsb-outs-q1',
                type: 'multiple-choice',
                question: 'Which of these does NOT result in an out?',
                options: [
                  'A fly ball caught before it touches the ground',
                  'A runner forced out at second on a ground ball',
                  'A batter taking ball four',
                  'A strikeout looking',
                ],
                correctIndex: 2,
                explanation: 'Ball four is a walk — the batter goes to first base safely. The other three are all standard ways to record an out.',
              },
              {
                id: 'bsb-outs-q2',
                type: 'fix-mistake',
                question: '"With two outs, you should always swing for the fences and try to hit a home run." What\'s wrong?',
                options: [
                  'Nothing — power is the only way to score with two outs',
                  'With two outs, putting the ball in play with a high-contact at-bat is usually higher value than swinging for power',
                  'You should always bunt with two outs',
                  'You can\'t score with two outs',
                ],
                correctIndex: 1,
                explanation: 'Two-out hitting prizes contact and on-base ability. Striking out wastes the inning entirely, while putting the ball in play extends innings and gives runners a chance to score.',
              },
            ],
          },
        ],
      },
      {
        id: 'bb-positions-101',
        title: 'Field Positions',
        icon: '🧤',
        requiredXp: 30,
        lessons: [
          {
            id: 'baseball-the-diamond',
            title: 'The Diamond',
            description: 'The nine positions on a baseball field.',
            conceptText: [
              'A baseball defense has 9 players. The infield: pitcher (P), catcher (C), first baseman (1B), second baseman (2B), shortstop (SS), and third baseman (3B).',
              'The outfield has three players: left fielder (LF), center fielder (CF), and right fielder (RF). The center fielder is usually the fastest and covers the most ground.',
              'Each position has a numeric scorekeeping code: 1=P, 2=C, 3=1B, 4=2B, 5=3B, 6=SS, 7=LF, 8=CF, 9=RF. So a "6-4-3 double play" means SS to 2B to 1B.',
            ],
            xpReward: 15,
            questions: [
              {
                id: 'bsb-dia-q1',
                type: 'diagram-identify',
                question: 'Study the diamond. Which infield position covers the gap between second and third base?',
                diagram: {
                  title: 'Baseball Diamond — Standard Defense',
                  sport: 'baseball',
                  players: [
                    { x: 50, y: 78, type: 'defense', label: 'C' },
                    { x: 50, y: 60, type: 'defense', label: 'P' },
                    { x: 72, y: 56, type: 'defense', label: '1B' },
                    { x: 60, y: 42, type: 'defense', label: '2B' },
                    { x: 40, y: 42, type: 'defense', label: 'SS' },
                    { x: 28, y: 56, type: 'defense', label: '3B' },
                    { x: 20, y: 22, type: 'defense', label: 'LF' },
                    { x: 50, y: 14, type: 'defense', label: 'CF' },
                    { x: 80, y: 22, type: 'defense', label: 'RF' },
                  ],
                  routes: [],
                },
                options: ['First Baseman', 'Shortstop', 'Center Fielder', 'Catcher'],
                correctIndex: 1,
                explanation: 'The shortstop covers the gap between 2B and 3B and is usually the most athletic infielder, handling the largest defensive area.',
              },
              {
                id: 'bsb-dia-q2',
                type: 'multiple-choice',
                question: 'In scorekeeping, a "6-4-3" double play means the ball traveled from which positions?',
                options: ['Pitcher → Catcher → First', 'Shortstop → Second → First', 'Third → Second → First', 'Center → Second → First'],
                correctIndex: 1,
                explanation: '6=SS, 4=2B, 3=1B. The shortstop fields the ball, flips to the second baseman covering the bag, who throws to first to complete the twin killing.',
              },
            ],
          },
          {
            id: 'baseball-battery',
            title: 'The Battery',
            description: 'Pitcher and catcher — the heartbeat of the defense.',
            conceptText: [
              'The "battery" is the pitcher-catcher combo. Together they decide what pitch to throw, where to throw it, and how to attack each hitter.',
              'The catcher gives signs (often using a wristband-decoded sequence in the modern game) to call the pitch type and location. The pitcher can shake off a sign to request a different one.',
              'A great battery reads hitters in real time — adjusting based on swings, takes, and body language. They control the tempo and sequence of every plate appearance.',
            ],
            xpReward: 15,
            questions: [
              {
                id: 'bsb-bat-q1',
                type: 'multiple-choice',
                question: 'What does it mean when a pitcher "shakes off" the catcher?',
                options: [
                  'The pitcher is signaling for a pickoff throw',
                  'The pitcher disagrees with the called pitch and wants a different one',
                  'The pitcher is calling for a mound visit',
                  'The pitcher is asking the umpire for a new ball',
                ],
                correctIndex: 1,
                explanation: 'A shake-off means the pitcher doesn\'t want to throw the suggested pitch. The catcher then cycles through other signs until the pitcher nods.',
              },
              {
                id: 'bsb-bat-tf1',
                type: 'true-false',
                question: 'The catcher is responsible for calling pitches and positioning the defense.',
                options: [],
                correctIndex: 0,
                correctAnswer: true,
                explanation: 'True. The catcher is the on-field defensive coordinator — calling pitches, shifting fielders, and reading hitters.',
              },
            ],
          },
        ],
      },
      {
        id: 'bb-rules-101',
        title: 'Rules & Gameplay',
        icon: '📜',
        requiredXp: 60,
        lessons: [
          {
            id: 'baseball-balls-vs-strikes',
            title: 'Balls vs Strikes',
            description: 'The count, the zone, and what every pitch means.',
            conceptText: [
              'A pitch in the strike zone (or swung at) is a strike. A pitch outside the zone that the batter takes is a ball. Three strikes = strikeout. Four balls = walk to first base.',
              'The "count" is the running tally of balls and strikes in an at-bat — always stated balls-first (e.g. "2-1" means 2 balls, 1 strike).',
              'Hitter\'s counts (3-1, 2-0, 3-0) favor the batter — the pitcher must throw a strike. Pitcher\'s counts (0-2, 1-2) favor the pitcher, who can waste pitches outside the zone trying to get the batter to chase.',
            ],
            xpReward: 15,
            questions: [
              {
                id: 'bsb-bvs-q1',
                type: 'multiple-choice',
                question: 'How many balls does it take to draw a walk?',
                options: ['3', '4', '5', '6'],
                correctIndex: 1,
                explanation: 'Four balls = a walk (also called a "base on balls"). The batter takes first base without swinging.',
              },
              {
                id: 'bsb-bvs-q2',
                type: 'multiple-choice',
                question: 'A 3-1 count is generally considered a:',
                options: ['Pitcher\'s count', 'Hitter\'s count', 'Neutral count', 'Full count'],
                correctIndex: 1,
                explanation: '3-1 is a classic hitter\'s count — the pitcher can\'t afford another ball, so he\'s likely to throw a fastball in the zone, and the hitter knows it.',
              },
              {
                id: 'bsb-bvs-tf1',
                type: 'true-false',
                question: 'A "full count" is 3 balls and 2 strikes.',
                options: [],
                correctIndex: 0,
                correctAnswer: true,
                explanation: 'True. 3-2 is the full count — the next called ball is a walk, the next called strike or swinging miss is a strikeout.',
              },
            ],
          },
          {
            id: 'baseball-strike-zone',
            title: 'The Strike Zone',
            description: 'What "in the zone" actually means.',
            conceptText: [
              'The strike zone is a 3D rectangle over home plate. Horizontally, it spans the width of the plate (17 inches). Vertically, it runs from the hollow beneath the kneecap to the midpoint between the shoulders and the top of the pants.',
              'Crucially, the zone moves with each batter — taller hitters have a taller zone. Umpires call the zone slightly differently from each other, which is why pitch framing by the catcher matters so much.',
              'A pitch only needs to nick the corner of the zone to be a strike. Pitchers who can hit corners (especially the low-and-away corner) are very hard to hit.',
            ],
            xpReward: 15,
            questions: [
              {
                id: 'bsb-zone-q1',
                type: 'multiple-choice',
                question: 'The vertical edges of the strike zone are defined by:',
                options: [
                  'The batter\'s height — top of the head to the toes',
                  'The midpoint of the torso down to the hollow beneath the kneecap',
                  'A fixed distance from the ground',
                  'The catcher\'s glove position',
                ],
                correctIndex: 1,
                explanation: 'The zone runs from the midpoint between the shoulders and the top of the pants down to the hollow beneath the kneecap, which is why the zone changes per batter.',
              },
              {
                id: 'bsb-zone-q2',
                type: 'fix-mistake',
                question: '"A pitch is only a strike if it crosses the middle of the plate." What\'s wrong?',
                options: [
                  'Nothing — only middle pitches are strikes',
                  'Any part of the ball nicking any part of the zone (including the corners) is a strike',
                  'Strikes only happen on swings',
                  'Only fastballs can be strikes',
                ],
                correctIndex: 1,
                explanation: 'A strike just needs the ball to touch any part of the zone — corners count. That\'s why pitchers paint the corners and catchers frame to steal extra strikes.',
              },
            ],
          },
        ],
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════
  // PATH 2 — HITTING, PITCHING & BASE RUNNING (Intermediate)
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'baseball-hitting-pitching',
    difficulty: 'intermediate',
    title: 'Hitting, Pitching & Base Running',
    description: 'The three core skills every player must understand.',
    color: 'accent',
    units: [
      {
        id: 'bb-hitting',
        title: 'Hitting Fundamentals',
        icon: '🏏',
        requiredXp: 0,
        lessons: [
          {
            id: 'baseball-stance-grip',
            title: 'Stance & Grip',
            description: 'Setting up to give yourself a chance.',
            conceptText: [
              'A balanced stance — feet shoulder-width, knees flexed, weight on the balls of the feet — lets you adjust to any pitch type and location.',
              'The grip should be firm but not tight. A common cue: "hold the bat like you\'re shaking hands." A grip too tight slows the swing; too loose loses control.',
              'Eyes stay level and focused on the pitcher\'s release point. The instant the ball leaves the hand is when great hitters start tracking — earlier reads = better swings.',
            ],
            xpReward: 20,
            questions: [
              {
                id: 'bsb-stance-q1',
                type: 'multiple-choice',
                question: 'Why is gripping the bat too tight a problem?',
                options: [
                  'It causes the ball to curve foul',
                  'It tenses the forearms and slows bat speed through the zone',
                  'It\'s actually ideal — tighter is always better',
                  'It only matters on bunts',
                ],
                correctIndex: 1,
                explanation: 'A death grip tenses the forearms and locks up the wrists, killing bat speed and quickness. A relaxed grip lets the wrists snap through the zone.',
              },
              {
                id: 'bsb-stance-tf1',
                type: 'true-false',
                question: 'Great hitters start tracking the ball at the pitcher\'s release point.',
                options: [],
                correctIndex: 0,
                correctAnswer: true,
                explanation: 'True. The earlier you pick up the ball, the more time you have to read pitch type and location — elite hitters lock onto the release point every pitch.',
              },
            ],
          },
          {
            id: 'baseball-plate-discipline',
            title: 'Plate Discipline',
            description: 'Why taking the right pitches wins at-bats.',
            conceptText: [
              'Plate discipline is the ability to swing at strikes and lay off balls. Hitters with elite discipline draw walks, work counts, and force pitchers to throw in the zone.',
              'Modern baseball values "chase rate" (how often you swing at pitches outside the zone) as a top hitter metric. Lower chase = better hitter, almost without exception.',
              'A common pro hitting cue: "swing at strikes, take balls, hit your pitch." Your pitch is your hot zone — fastballs in your wheelhouse, breaking balls you can drive. Anything else, take.',
            ],
            xpReward: 20,
            questions: [
              {
                id: 'bsb-disc-q1',
                type: 'multiple-choice',
                question: 'A hitter with a low "chase rate" is one who:',
                options: [
                  'Rarely swings at pitches outside the strike zone',
                  'Steals a lot of bases',
                  'Hits a lot of home runs',
                  'Strikes out frequently',
                ],
                correctIndex: 0,
                explanation: 'Chase rate measures how often a hitter swings at pitches outside the strike zone. Low chase = excellent plate discipline.',
              },
            ],
          },
        ],
      },
      {
        id: 'bb-pitching',
        title: 'Pitching Basics',
        icon: '🥎',
        requiredXp: 40,
        lessons: [
          {
            id: 'baseball-how-pitch-counts-work',
            title: 'How Pitch Counts Work',
            description: 'Why coaches obsess over pitch counts.',
            conceptText: [
              'A pitcher\'s "pitch count" is the total number of pitches thrown in an outing. MLB starters typically work in the 90–110 pitch range; once they cross that, performance drops as fatigue sets in.',
              'Youth and high school baseball has strict pitch count limits (often 75–110 per game depending on age) and required rest days afterward to protect young arms.',
              'The pitch count drives bullpen decisions: a starter at 95 pitches with the heart of the order coming up is usually pulled for a fresh reliever, even if he\'s pitching well.',
            ],
            xpReward: 20,
            questions: [
              {
                id: 'bsb-pc-q1',
                type: 'multiple-choice',
                question: 'Why do pitch counts matter so much for starters?',
                options: [
                  'They\'re just a stat — they don\'t affect strategy',
                  'Performance and arm health both decline as pitch count climbs, especially past 100',
                  'Starters get extra pay for pitches over 100',
                  'The umpire stops the game at 100 pitches',
                ],
                correctIndex: 1,
                explanation: 'Both performance and arm-injury risk rise sharply as pitch count climbs past ~100, which is why managers track every pitch and have relievers warming early.',
              },
              {
                id: 'bsb-pc-tf1',
                type: 'true-false',
                question: 'Youth baseball has rules limiting how many pitches a player can throw and requiring rest days.',
                options: [],
                correctIndex: 0,
                correctAnswer: true,
                explanation: 'True. Pitch-smart rules (Little League, MLB Pitch Smart) protect young arms by capping daily pitches and mandating rest days based on workload.',
              },
            ],
          },
          {
            id: 'baseball-pitch-types',
            title: 'Pitch Types',
            description: 'Fastballs, breaking balls, off-speed — and why mix matters.',
            conceptText: [
              'Pitches fall into three families: fastballs (4-seam, 2-seam, cutter, sinker), breaking balls (curveball, slider, sweeper), and off-speed (changeup, splitter).',
              'Each family attacks differently: fastballs overpower with speed, breaking balls fool with horizontal/vertical break, off-speed disrupts timing by looking like a fastball but arriving slower.',
              'Elite pitchers don\'t throw their best pitch every time — they sequence. A 96 mph fastball feels even faster after an 82 mph changeup. Variety + location wins at-bats.',
            ],
            xpReward: 20,
            questions: [
              {
                id: 'bsb-pt-q1',
                type: 'multiple-choice',
                question: 'A changeup is most effective because:',
                options: [
                  'It\'s the fastest pitch in baseball',
                  'It looks like a fastball out of the hand but arrives ~10 mph slower, disrupting hitter timing',
                  'It curves more than any other pitch',
                  'It only works against left-handed hitters',
                ],
                correctIndex: 1,
                explanation: 'The changeup\'s deception is everything — it mimics fastball arm action but is much slower, so the hitter swings early and misses (or weakly grounds out).',
              },
            ],
          },
        ],
      },
      {
        id: 'bb-baserunning',
        title: 'Base Running',
        icon: '👟',
        requiredXp: 80,
        lessons: [
          {
            id: 'baseball-base-running-basics',
            title: 'Base Running Basics',
            description: 'Reading the play and taking the extra base.',
            conceptText: [
              'Smart base running is about reading the situation: number of outs, score, the defender\'s arm strength, and where the ball is hit.',
              'On a fly ball with less than 2 outs, runners "tag up" — they stay on the base until the ball is caught, then sprint to the next base. The fielder has to catch then throw, often beating only slow runners.',
              'A general rule: with 2 outs, runners go on contact (the at-bat is over either way). With less than 2 outs, runners are more cautious to avoid being doubled up on a line drive.',
            ],
            xpReward: 20,
            questions: [
              {
                id: 'bsb-br-q1',
                type: 'multiple-choice',
                question: 'A runner on second base "tags up" on a fly ball. What does this mean?',
                options: [
                  'They run as soon as the ball is hit',
                  'They wait on the base until the ball is caught, then advance',
                  'They have to slide into the next base',
                  'They are out automatically',
                ],
                correctIndex: 1,
                explanation: 'Tagging up means staying on the base until the fly ball is caught, then trying to advance. If they leave early on a caught fly, they can be doubled off.',
              },
              {
                id: 'bsb-br-tf1',
                type: 'true-false',
                question: 'With 2 outs, base runners typically run on contact.',
                options: [],
                correctIndex: 0,
                correctAnswer: true,
                explanation: 'True. With 2 outs, the inning ends on the third out anyway — runners go full speed on contact to maximize their chance of scoring.',
              },
            ],
          },
        ],
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════
  // PATH 3 — STRATEGY & SITUATIONAL BASEBALL (Advanced)
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'baseball-strategy',
    difficulty: 'advanced',
    title: 'Strategy & Situational',
    description: 'Defensive concepts, MLB strategy, and advanced game theory.',
    color: 'accent',
    units: [
      {
        id: 'bb-defense',
        title: 'Defensive Concepts',
        icon: '🛡️',
        requiredXp: 0,
        lessons: [
          {
            id: 'baseball-double-play',
            title: 'What Is a Double Play?',
            description: 'The most demoralizing play in baseball.',
            conceptText: [
              'A double play (DP) records two outs on a single batted ball — the most efficient defensive play in baseball.',
              'The classic DP is the 6-4-3: with a runner on first and a ground ball to the shortstop, the SS flips to the 2B (force at second), who pivots and fires to first to retire the batter.',
              'Defenses set up for DPs by playing infielders at "double-play depth" — closer to second base — giving them shorter throws and quicker exchanges.',
            ],
            xpReward: 25,
            questions: [
              {
                id: 'bsb-dp-q1',
                type: 'multiple-choice',
                question: 'What\'s the most common double-play sequence in baseball?',
                options: ['1-2-3 (P-C-1B)', '6-4-3 (SS-2B-1B)', '4-2-6 (2B-C-SS)', '7-8-9 (LF-CF-RF)'],
                correctIndex: 1,
                explanation: 'The 6-4-3 (shortstop to second baseman to first baseman) is the textbook double play, executed thousands of times each MLB season.',
              },
              {
                id: 'bsb-dp-tf1',
                type: 'true-false',
                question: 'Infielders play closer to second base in "double play depth."',
                options: [],
                correctIndex: 0,
                correctAnswer: true,
                explanation: 'True. Moving closer to second cuts the distance for the first throw, making the turn quicker and giving the team a real shot at two outs.',
              },
            ],
          },
          {
            id: 'baseball-defensive-shifts',
            title: 'Defensive Shifts Explained',
            description: 'Why teams move three infielders to one side.',
            conceptText: [
              'A defensive shift moves fielders out of standard positions based on a hitter\'s tendencies. A pull-heavy lefty might see three infielders on the right side of second base.',
              'MLB recently restricted extreme shifts — at least two infielders must now be on each side of second base, and all four must have both feet in the infield dirt at pitch release.',
              'Even within the new rules, teams still position based on tendencies: a third baseman might shade toward the line, an outfielder might play 30 feet shallower for a weak hitter.',
            ],
            xpReward: 25,
            questions: [
              {
                id: 'bsb-shift-q1',
                type: 'multiple-choice',
                question: 'Under current MLB rules, how many infielders must be on each side of second base?',
                options: ['1', '2', '3', 'No rule — any alignment is allowed'],
                correctIndex: 1,
                explanation: 'MLB now requires at least two infielders on each side of second base at pitch release, eliminating the most extreme overshifts.',
              },
              {
                id: 'bsb-shift-q2',
                type: 'fix-mistake',
                question: '"Defensive shifts only work against home-run hitters." What\'s wrong?',
                options: [
                  'Nothing — that\'s exactly right',
                  'Shifts target hitters with strong pull tendencies on ground balls and line drives, not just power hitters',
                  'Shifts only work in night games',
                  'Shifts have been banned entirely',
                ],
                correctIndex: 1,
                explanation: 'Shifts target spray charts — where a hitter actually puts the ball in play. A pull-heavy contact hitter is shifted just as aggressively as a slugger.',
              },
            ],
          },
        ],
      },
      {
        id: 'bb-mlb-strategy',
        title: 'MLB Strategy',
        icon: '🧠',
        requiredXp: 40,
        lessons: [
          {
            id: 'baseball-bullpen-roles',
            title: 'Bullpen Roles',
            description: 'Setup man, closer, and the modern bullpen game.',
            conceptText: [
              'The modern bullpen is highly specialized. The closer pitches the 9th with a lead. The setup man (often the 8th-inning guy) bridges to the closer. Long relievers cover multi-inning stints.',
              'High-leverage relievers come in during the most critical moments — bases loaded, tie game, top of the order — regardless of inning. They\'re often a team\'s best reliever after the closer.',
              'A "bullpen game" is when a team uses 5–6 relievers instead of a starter, each pitching 1–3 innings. It\'s common in the playoffs when every game matters.',
            ],
            xpReward: 25,
            questions: [
              {
                id: 'bsb-bull-q1',
                type: 'multiple-choice',
                question: 'A "high-leverage" reliever is brought into the game when:',
                options: [
                  'The team is up by 10 runs',
                  'The score and base/out situation make every pitch critical',
                  'It\'s the first inning',
                  'The starter has thrown 50 pitches',
                ],
                correctIndex: 1,
                explanation: 'High-leverage situations (close games, bases loaded, key hitters) demand the team\'s best available arm — even if it\'s only the 7th inning.',
              },
            ],
          },
          {
            id: 'baseball-era-stats',
            title: 'ERA and Baseball Stats',
            description: 'Reading the numbers that explain the game.',
            conceptText: [
              'ERA (Earned Run Average) is the average number of earned runs a pitcher gives up per 9 innings. A sub-3.00 ERA is excellent; sub-2.00 is elite.',
              'For hitters, the classic slash line is AVG/OBP/SLG. AVG is hits per at-bat. OBP adds walks. SLG measures total bases per at-bat. OPS adds OBP + SLG into one number — .800+ is very good, .900+ is elite.',
              'Modern stats include WAR (Wins Above Replacement) for overall value, FIP (Fielding Independent Pitching) for true pitcher quality, and wRC+ for park-adjusted hitting (100 = league average, 150 = 50% better).',
            ],
            xpReward: 25,
            questions: [
              {
                id: 'bsb-era-q1',
                type: 'multiple-choice',
                question: 'What does ERA measure?',
                options: [
                  'A pitcher\'s career strikeouts',
                  'The average number of earned runs allowed per 9 innings pitched',
                  'A pitcher\'s salary',
                  'The number of innings pitched',
                ],
                correctIndex: 1,
                explanation: 'ERA = (earned runs × 9) / innings pitched. It standardizes runs allowed to a per-9-innings rate, making it easy to compare pitchers across roles.',
              },
              {
                id: 'bsb-era-q2',
                type: 'multiple-choice',
                question: 'OPS is the sum of which two stats?',
                options: ['AVG + SLG', 'OBP + SLG', 'AVG + OBP', 'HR + RBI'],
                correctIndex: 1,
                explanation: 'OPS = On-Base Percentage + Slugging. It captures both how often you reach base AND how much power you produce in one quick number.',
              },
            ],
          },
        ],
      },
      {
        id: 'bb-situational',
        title: 'Advanced Situational',
        icon: '🎯',
        requiredXp: 80,
        lessons: [
          {
            id: 'baseball-hit-and-run',
            title: 'Hit-and-Run',
            description: 'A coordinated runner-batter play to disrupt the defense.',
            conceptText: [
              'On a hit-and-run, the runner takes off from first as the pitcher delivers, and the batter is required to swing at the pitch — almost regardless of location — to protect the runner.',
              'The goals: stay out of a double play, advance the runner two bases on a single, and create defensive holes (the SS or 2B vacates the base to cover second).',
              'The right count matters: managers love 1-1, 2-1, and 3-1 counts because the pitcher is most likely to throw a fastball strike — the easiest pitch for the batter to put in play.',
            ],
            xpReward: 25,
            questions: [
              {
                id: 'bsb-hr-q1',
                type: 'multiple-choice',
                question: 'On a hit-and-run, what is the batter expected to do?',
                options: [
                  'Take the pitch no matter what',
                  'Bunt down the third base line',
                  'Swing at the pitch — even a borderline one — to protect the runner',
                  'Step out of the box',
                ],
                correctIndex: 2,
                explanation: 'The batter swings to put the ball in play, ideally to the right side where the SS or 2B has vacated. Taking the pitch leaves the runner exposed at second.',
              },
              {
                id: 'bsb-hr-tf1',
                type: 'true-false',
                question: 'A hit-and-run is most often called on hitter-friendly counts when a fastball is likely.',
                options: [],
                correctIndex: 0,
                correctAnswer: true,
                explanation: 'True. Counts like 1-1, 2-1, and 3-1 increase the odds of a fastball strike, making it easier for the hitter to put the ball in play and protect the runner.',
              },
            ],
          },
        ],
      },
    ],
  },
];

// ── Helpers (mirrors basketballLessons.ts API) ─────────────────────

export function getBaseballTotalLessons(): number {
  let total = 0;
  for (const path of baseballLearningPaths) {
    for (const unit of path.units) {
      total += unit.lessons.length;
    }
  }
  return total;
}

export function findBaseballLesson(
  lessonId: string
): { path: LearningPath; unit: Unit; lesson: Lesson } | null {
  for (const path of baseballLearningPaths) {
    for (const unit of path.units) {
      for (const lesson of unit.lessons) {
        if (lesson.id === lessonId) {
          return { path, unit, lesson };
        }
      }
    }
  }
  return null;
}

export function getNextBaseballLesson(currentLessonId: string, xp: number): Lesson | null {
  let found = false;
  for (const path of baseballLearningPaths) {
    for (const unit of path.units) {
      if (xp < unit.requiredXp) continue;
      for (const lesson of unit.lessons) {
        if (found) return lesson;
        if (lesson.id === currentLessonId) found = true;
      }
    }
  }
  return null;
}
